import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '@/lib/auth/useUser';
import { useActiveOrg } from '@/stores/activeOrgStore';
import { RoleGuard } from '@/lib/auth/RoleGuard';
import { apiClient } from '@/lib/http/apiClient';
import { Spinner } from '@/components/atoms/Spinner';
import { DataTable } from '@/components/organisms/DataTable';
import { AuthenticatedShell } from '@/features/app/AuthenticatedShell';

export const Route = createFileRoute('/settings/members')({ component: MembersPage });

interface OrgMember {
  membershipId: string;
  userId: string;
  email: string;
  fullName: string;
  role: string;
  joinedAt: string;
}

function MembersPage() {
  return (
    <RoleGuard allow={['DeveloperOrgAdmin', 'FunderOrgAdmin']}>
      <MembersContent />
    </RoleGuard>
  );
}

function MembersContent() {
  const me = useUser();
  const orgId = useActiveOrg((s) => s.activeOrgId) ?? me.data?.memberships[0]?.organization.id;
  const members = useQuery({
    queryKey: ['org-members', orgId],
    enabled: !!orgId,
    queryFn: async () => (await apiClient.get<OrgMember[]>(`/api/v1/organizations/${orgId}/members`)).data,
  });

  if (!orgId || members.isLoading) {
    return (
      <AuthenticatedShell activeNavId="organization">
        <div className="p-8"><Spinner label="Loading members" /></div>
      </AuthenticatedShell>
    );
  }

  return (
    <AuthenticatedShell activeNavId="organization">
      <div className="mx-auto max-w-5xl px-8 py-10">
        <h1 className="font-display text-display tracking-tight text-foreground">
          Org <span className="italic text-primary">members</span>.
        </h1>
        <p className="mt-3 text-body-sm text-muted-foreground">People with access to this organization.</p>
        <div className="mt-8 rounded-lg border border-border bg-card p-4">
          <DataTable<OrgMember>
            rowKey={(r) => r.membershipId}
            columns={[
              { header: 'Name',     cell: (r) => r.fullName },
              { header: 'Email',    cell: (r) => r.email },
              { header: 'Role',     cell: (r) => r.role },
              { header: 'Joined',   cell: (r) => new Date(r.joinedAt).toLocaleDateString() },
            ]}
            rows={members.data ?? []}
            emptyMessage="No members yet."
          />
        </div>
      </div>
    </AuthenticatedShell>
  );
}
